import { useEffect, useId, useMemo, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import {
  Button,
  Checkbox,
  Combobox,
  Field,
  Input,
  Modal,
  MultiCombobox,
  Select,
  Textarea,
} from '@/components/ui';
import type { ComboboxOption, MultiComboboxOption } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { TARGET_BILLING_REGIONS, useBillingRegionLabels } from '@/lib/billing-region';
import {
  NEW_BUSINESS_GATE_BASIS_OPTIONS,
  TEMPLATE_EDIT_REACHES_ASSIGNMENTS_NOTE,
  isUnbandedRegion,
  modelExplanation,
} from '@/lib/commissions';
import type {
  CommissionGateShape,
  CommissionModel,
  CommissionScheme,
  CommissionSchemeCreateInput,
  CommissionSchemeUpdateInput,
  Currency,
  NewBusinessGateBasis,
  TargetBillingRegion,
} from '@/lib/types';

/* ---------------------------------------------------------------------------
 * THE COMMISSION SCHEME BUILDER — a TEMPLATE, naming nobody and no year.
 *
 * Contract facts, read out of `migrations/202_create_d_commission_schemes.sql`
 * and `src/modules/commissions/commissions.schema.ts` in `emerge-backend-app`:
 *
 *  1. ⛔ A PERCENTAGE IS `"2.0000"` MEANING TWO PER CENT, NEVER `"0.02"`. The two
 *     spellings are a factor of a hundred apart and BOTH produce a well-formed
 *     money figure — the run pays 1,160,000 instead of 11,600, or 116 instead of
 *     11,600, and there is no plausibility check on a commission figure that
 *     catches either. Every field name ends `Percent` for that reason, the unit
 *     is in the label AND in the control, and `toPercentPayload` normalises what
 *     was typed to four decimals WITHOUT EVER TOUCHING A FLOAT.
 *
 *  2. ⛔ `billingRegions` IS A WHOLE-SET REPLACE. `[…]` replaces, `[]` CLEARS,
 *     an absent key leaves it alone, `null` is a 422. So on an EDIT the key is
 *     sent only when the set actually moved — a modal that rebuilt its PATCH
 *     from its own state would send `[]` for a picker nobody opened and turn a
 *     four-band scheme into a personal-only one, with a `200` and no figure on
 *     screen moving until the next run. This repo has already paid for that
 *     exact failure on `CompanyProjectsModal`.
 *
 *  3. ⚠️ AN EMPTY BAND LIST IS A LEGAL PERSONAL-ONLY SCHEME, not an unfinished
 *     one, and is NOT refused. It is also indistinguishable from somebody having
 *     forgotten to pick a region, which is why the consequence is stated at the
 *     control — see `emptyBandsNote`.
 *
 *  4. ⚠️ `'none'` IS A REAL BAND — the companies with no country of record, 51
 *     live rows — and never the same thing as `[]`. One is a population, the
 *     other is the absence of one.
 *
 *  5. PATCH sends ONLY what changed, built from an explicit key list and never
 *     from a spread record. The body is `.strict()`, and an empty patch is a
 *     422, so Save is disabled when nothing moved.
 *
 *  6. ⚠️ THE CURRENCY PICKER SENDS `?isActive=true`. Only 3 of ~164 currency
 *     rows are active; a picker that omits the filter offers 161 values no new
 *     scheme should carry. But a STORED code is rendered whatever the master
 *     says — blanking a value because it is missing from the active list is how
 *     the next save silently reassigns it.
 *
 *  7. 🔴 `updateCommissionScheme` ANSWERS `{data, warning}` AND THE WARNING IS
 *     THE FEATURE. See `SavedWarning` below.
 *
 * Every scalar is held as a STRING so the loaded record and the live form
 * compare key-by-key with `!==`; `billingRegions` is a string array compared as
 * a set, and the two flags are real booleans.
 * ------------------------------------------------------------------------- */

export interface SchemeFormModalProps {
  open: boolean;
  /**
   * Absent (or null) = create, a record = edit. Typed `CommissionScheme | null`
   * rather than plain optional so a `useState<CommissionScheme | null>(null)`
   * passes straight through.
   */
  scheme?: CommissionScheme | null;
  onClose: () => void;
  /** Called after a successful save with the server's record. */
  onSaved: (saved: CommissionScheme) => void;
}

/* -------------------------------------------------------------------------- */
/* Form state                                                                 */
/* -------------------------------------------------------------------------- */

interface FormState {
  name: string;
  description: string;
  /** `f_commission_models.id` as a string, so it compares with the option value. */
  modelId: string;
  /** `f_commission_gate_shapes.id`, likewise. */
  gateShapeId: string;
  /** `a` — applied to the revenue this lead brought in. `2` means two per cent. */
  personalRatePercent: string;
  /** `z` — applied to the whole regional revenue. */
  regionalRatePercent: string;
  /** `x` — the share of the personal target that unlocks the pool. */
  personalGatePercent: string;
  /** `y` — the share of the regional target that unlocks the pool. */
  regionalGatePercent: string;
  /**
   * Applied to the account growth over the previous period, on top of the
   * model result. `'0'` (the column default) pays nothing extra.
   *
   * ⚠️ `''` on an EDIT means the record came from an older API that does not
   * carry the key — the box stays blank, is not validated while untouched, and
   * is never sent unless somebody types into it.
   */
  newBusinessRatePercent: string;
  /**
   * New business gate (owner 2026-10-28). `''` on an EDIT = an older API that
   * does not carry the key: every gate control is hidden and nothing is sent.
   */
  newBusinessGateBasis: NewBusinessGateBasis | '';
  /** `''` = same as the scheme's personal gate (`null` on the wire). */
  newBusinessPersonalGatePercent: string;
  /** `''` = same as the scheme's regional gate (`null` on the wire). */
  newBusinessRegionalGatePercent: string;
  /** `''` = same as the scheme's gate shape (`null` on the wire). */
  newBusinessGateShapeId: string;
  /**
   * Months back from the end of the period a run looks for recoverable revenue.
   *
   * ⛔ `'0'` IS A REAL CONFIGURATION meaning no clawback at all, and is not the
   * same as an empty box. Held as a string so the two stay apart, exactly as
   * `capacity` does on a project.
   */
  clawbackWindowMonths: string;
  /** The PAYMENT currency code. Nothing converts it on the way out. */
  currency: string;
  includesDraftInvoices: boolean;
  /** Band slugs. `[]` is a legal personal-only scheme — see rule 3. */
  billingRegions: string[];
  isActive: boolean;
}

/**
 * ⚠️ EVERY KEY HERE IS A WRITE KEY, SPELLED EXACTLY AS THE API SPELLS IT, and
 * `KNOWN_WRITE_KEYS` is derived from this object rather than written out a
 * second time. `routeFieldErrors` partitions a 422 on that list, so a key that
 * drifted from the controls would send its refusal to the form-level banner
 * while still counting towards *"one field needs attention, highlighted
 * below"* — with nothing highlighted.
 */
function emptyValues(): FormState {
  return {
    name: '',
    description: '',
    modelId: '',
    gateShapeId: '',
    personalRatePercent: '',
    regionalRatePercent: '',
    personalGatePercent: '',
    regionalGatePercent: '',
    // The column's own default (migration 334): existing pay is unchanged.
    newBusinessRatePercent: '0',
    // The column's own default (migration 338): today's gate behaviour.
    newBusinessGateBasis: 'scheme',
    newBusinessPersonalGatePercent: '',
    newBusinessRegionalGatePercent: '',
    newBusinessGateShapeId: '',
    // The column's own default, not a taste: migration 202 carries DEFAULT 12.
    clawbackWindowMonths: '12',
    currency: '',
    // ⚠️ The direction that CANNOT OVERPAY, matching the column's DEFAULT 0.
    includesDraftInvoices: false,
    billingRegions: [],
    isActive: true,
  };
}

function recordForm(record: CommissionScheme): FormState {
  return {
    name: record.name,
    description: record.description ?? '',
    modelId: String(record.model.id),
    gateShapeId: String(record.gateShape.id),
    personalRatePercent: record.personalRatePercent,
    regionalRatePercent: record.regionalRatePercent,
    personalGatePercent: record.personalGatePercent,
    regionalGatePercent: record.regionalGatePercent,
    // `undefined` = an older API, never 0 — see the FormState note.
    newBusinessRatePercent: record.newBusinessRatePercent ?? '',
    newBusinessGateBasis: record.newBusinessGateBasis ?? '',
    newBusinessPersonalGatePercent: record.newBusinessPersonalGatePercent ?? '',
    newBusinessRegionalGatePercent: record.newBusinessRegionalGatePercent ?? '',
    newBusinessGateShapeId:
      record.newBusinessGateShape === null || record.newBusinessGateShape === undefined
        ? ''
        : String(record.newBusinessGateShape.id),
    clawbackWindowMonths: String(record.clawbackWindowMonths),
    currency: record.currency,
    includesDraftInvoices: record.includesDraftInvoices,
    billingRegions: [...record.billingRegions],
    isActive: record.isActive,
  };
}

const KNOWN_WRITE_KEYS = Object.keys(emptyValues());

const LABELS: Record<string, string> = {
  name: 'Name',
  description: 'Description',
  modelId: 'Model',
  gateShapeId: 'Gate shape',
  personalRatePercent: 'Personal rate (%)',
  regionalRatePercent: 'Regional rate (%)',
  personalGatePercent: 'Personal gate (%)',
  regionalGatePercent: 'Regional gate (%)',
  newBusinessRatePercent: 'New business rate (%)',
  newBusinessGateBasis: 'New business gate',
  newBusinessPersonalGatePercent: 'New business personal gate (%)',
  newBusinessRegionalGatePercent: 'New business regional gate (%)',
  newBusinessGateShapeId: 'New business gate shape',
  clawbackWindowMonths: 'Clawback window (months)',
  currency: 'Payment currency',
  includesDraftInvoices: 'Count draft invoices',
  billingRegions: 'Billing bands measured',
  isActive: 'Active',
};

const NAME_MAX = 120;
const DESCRIPTION_MAX = 500;
/** Migration 202's own ceiling: 50 years is the widest sayable answer. */
const CLAWBACK_WINDOW_MAX_MONTHS = 600;

/* -------------------------------------------------------------------------- */
/* Percentages — exact, and never through a float                             */
/* -------------------------------------------------------------------------- */

/** `DECIMAL(7,4)`: at most 3 integer digits and 4 decimals, no sign. */
const PERCENT_PATTERN = /^\d{1,3}(\.\d{1,4})?$/;

const PERCENT_MESSAGE =
  'Enter a percentage from 0 to 100 with at most 4 decimal places. 2 per cent is 2, never 0.02.';

/**
 * What was typed, restated as the column's exact `DECIMAL(7,4)` string.
 *
 * ⛔ STRING SURGERY, NEVER `Number(x).toFixed(4)`. A commission rate is a figure
 * somebody is PAID against and `0.1 + 0.2` is not `0.3`; the server binds the
 * string it is handed straight to the driver, so whatever leaves here is what
 * the run multiplies revenue by. Padding the fraction cannot round and cannot
 * drift.
 *
 * ⚠️ The server would accept `"2"` unchanged — the pattern permits it. Sending
 * `"2.0000"` is deliberate: it is the spelling the column stores and the one the
 * statement freezes, so a reader comparing a form to a statement sees one
 * convention rather than two.
 */
function toPercentPayload(raw: string): string {
  const trimmed = raw.trim();
  const dot = trimmed.indexOf('.');
  const whole = dot === -1 ? trimmed : trimmed.slice(0, dot);
  const fraction = dot === -1 ? '' : trimmed.slice(dot + 1);
  return `${whole === '' ? '0' : whole}.${`${fraction}0000`.slice(0, 4)}`;
}

/**
 * A percentage refusal, or `undefined`.
 *
 * ⚠️ THE `<= 100` TEST PARSES, AND THAT IS SAFE HERE FOR A STATED REASON. The
 * pattern already caps the string at three integer digits and four decimals, so
 * the parsed number is at most 999.9999 — far inside exact float range — and the
 * parse is used ONLY for a bound comparison. The STRING is what is sent.
 * Parsing a rate for ARITHMETIC is the thing this module never does; parsing one
 * to ask whether it is above a hundred is not the same act, and the backend
 * schema makes the identical distinction at the identical bound.
 */
function validatePercent(raw: string): string | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') return 'A rate is required.';
  if (!PERCENT_PATTERN.test(trimmed)) return PERCENT_MESSAGE;
  if (Number(trimmed) > 100) return PERCENT_MESSAGE;
  return undefined;
}

/** A nullable override: blank = `null` (same as the scheme), else the payload form. */
function toOptionalPercentPayload(raw: string): string | null {
  return raw.trim() === '' ? null : toPercentPayload(raw);
}

/** Which new business gate overrides the chosen basis reads. */
function gateBasisUses(basis: FormState['newBusinessGateBasis']): {
  personal: boolean;
  regional: boolean;
  shape: boolean;
} {
  const personal = basis === 'personal' || basis === 'both';
  const regional = basis === 'regional' || basis === 'both';
  return { personal, regional, shape: personal || regional };
}

/* -------------------------------------------------------------------------- */
/* The edit warning                                                           */
/* -------------------------------------------------------------------------- */

/** What the modal renders after a save that reached live assignments. */
interface SavedWarning {
  readonly message: string | null;
  /** One line per assignment this edit reaches — a name and a year. */
  readonly items: readonly string[];
}

/**
 * 🔴 THE WARNING IS THE FEATURE, AND IT IS READ THROUGH A RUNTIME GUARD.
 *
 * Editing a scheme does not touch a statement that has already been computed —
 * every term is FROZEN onto the statement at compute, which is what makes an
 * approved run auditable. It DOES silently change what every live assignment on
 * this scheme will compute the next time a run is made: a `200`, a saved form,
 * and a payout that moves for people nobody was looking at.
 *
 * ⛔ `null` IS NOT THE SAME AS THE KEY BEING ABSENT and nothing folds them: the
 * server answering `null` LOOKED and found nothing this edit reaches. This
 * reader returns `null` for both because a client that cannot tell them apart
 * must say nothing rather than invent a reassurance — but it never renders an
 * all-clear either way.
 *
 * ⚠️ `unknown` DELIBERATELY, and this is the `detailPayload` shape rather than
 * laziness: `SchemeEditWarning` is owned by another module in this repo and by
 * the service in `emerge-backend-app`, and the envelope carries a shape this
 * file has no business pinning. A cast would compile against whatever it was
 * asserted to be and print `undefined` the day the shape moved; a guard degrades
 * to "we could not read it" and renders nothing, which is the honest direction
 * for a consequence notice.
 */
function readSchemeWarning(warning: unknown): SavedWarning | null {
  if (warning === null || warning === undefined) return null;
  if (typeof warning === 'string') {
    return warning.trim() === '' ? null : { message: warning, items: [] };
  }
  if (typeof warning !== 'object') return null;

  const source = warning as Record<string, unknown>;
  const message = typeof source.message === 'string' ? source.message : null;

  const raw = Array.isArray(source.assignments) ? source.assignments : [];
  const items: string[] = [];
  for (const entry of raw) {
    if (typeof entry === 'string') {
      items.push(entry);
      continue;
    }
    if (typeof entry !== 'object' || entry === null) continue;
    const row = entry as Record<string, unknown>;
    const who = typeof row.userName === 'string' ? row.userName : null;
    const when = typeof row.periodName === 'string' ? row.periodName : null;
    if (who && when) items.push(`${who} — ${when}`);
    else if (who) items.push(who);
    else if (when) items.push(when);
  }

  if (message === null && items.length === 0) return null;
  return { message, items };
}

/* -------------------------------------------------------------------------- */
/* Payload shapes                                                             */
/* -------------------------------------------------------------------------- */

/*
 * ⚠️ BUILT AS TYPED OBJECTS AND HANDED STRAIGHT TO THE API METHOD, never cast.
 * `CommissionSchemeCreateInput` / `CommissionSchemeUpdateInput` live in
 * `src/lib/types.ts`, which this file does not own, so the assignment below is
 * the one instrument that checks these keys against the real write contract. A
 * double assertion would have compiled over a drifted key and sent a 422 nobody
 * could route.
 */

interface SchemePatch {
  name?: string;
  description?: string | null;
  modelId?: number;
  gateShapeId?: number;
  personalRatePercent?: string;
  regionalRatePercent?: string;
  personalGatePercent?: string;
  regionalGatePercent?: string;
  newBusinessRatePercent?: string;
  newBusinessGateBasis?: NewBusinessGateBasis;
  newBusinessPersonalGatePercent?: string | null;
  newBusinessRegionalGatePercent?: string | null;
  newBusinessGateShapeId?: number | null;
  clawbackWindowMonths?: number;
  currency?: string;
  includesDraftInvoices?: boolean;
  billingRegions?: TargetBillingRegion[];
  isActive?: boolean;
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const seen = new Set(b);
  return a.every((value) => seen.has(value));
}

/* -------------------------------------------------------------------------- */
/* The modal                                                                  */
/* -------------------------------------------------------------------------- */

export function SchemeFormModal({ open, scheme, onClose, onSaved }: SchemeFormModalProps) {
  const record = scheme ?? null;
  const isEdit = record !== null;
  const formId = useId();

  const [form, setForm] = useState<FormState>(() =>
    record ? recordForm(record) : emptyValues(),
  );
  const [baseline, setBaseline] = useState<FormState>(() =>
    record ? recordForm(record) : emptyValues(),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedWarning, setSavedWarning] = useState<SavedWarning | null>(null);

  const [models, setModels] = useState<readonly CommissionModel[]>([]);
  const [gateShapes, setGateShapes] = useState<readonly CommissionGateShape[]>([]);
  const [currencies, setCurrencies] = useState<readonly Currency[]>([]);
  const [mastersFailed, setMastersFailed] = useState(false);

  const labelOfRegion = useBillingRegionLabels();

  /*
   * RE-SEED WHENEVER THE DIALOG OPENS, OR OPENS ON A DIFFERENT RECORD — during
   * render, not in an effect.
   *
   * ⚠️ THE CALL SITE KEEPS THIS MOUNTED ACROSS OPENS (`open={formOpen}` on an
   * unconditionally rendered modal), so lazy initial state alone would show the
   * PREVIOUSLY edited scheme's rates under the new one's name — a form full of
   * somebody else's percentages, all of them plausible.
   *
   * ⚠️ AND IT IS DELIBERATELY NOT AN EFFECT. An effect re-seeds AFTER a paint, so
   * the stale figures are briefly on screen and every derived value — `patch`,
   * `dirty`, `submitDisabled` — is computed against them for that render. React's
   * own adjust-state-during-render pattern discards the render instead, and the
   * guard is what makes it terminate.
   */
  const openIdentity = open ? (record ? `edit:${record.id}` : 'new') : 'closed';
  const [seededFor, setSeededFor] = useState(openIdentity);
  if (seededFor !== openIdentity) {
    const next = record ? recordForm(record) : emptyValues();
    setSeededFor(openIdentity);
    setForm(next);
    setBaseline(next);
    setErrors({});
    setFormError(null);
    setSavedWarning(null);
  }

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();

    Promise.all([
      api.listCommissionModels(controller.signal),
      api.listCommissionGateShapes(controller.signal),
      /*
       * ⚠️ `isActive: true` IS OPT-IN AND IS LOAD-BEARING HERE: 161 of the ~164
       * currency rows are retired, so a picker that omitted it would offer a
       * value no new scheme should carry. `perPage: 250` is the master's own
       * ceiling, so the active list arrives whole.
       */
      api.listCurrencies({ isActive: true, perPage: 250 }, controller.signal),
    ])
      .then(([modelResult, gateResult, currencyResult]) => {
        if (controller.signal.aborted) return;
        setModels(modelResult.data);
        setGateShapes(gateResult.data);
        setCurrencies(currencyResult.data);
        setMastersFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        /*
         * "We do not know", never "there are none". An empty picker over a read
         * that never came back is a statement about the business made on the
         * strength of a failed request.
         */
        setMastersFailed(true);
      });

    return () => controller.abort();
  }, [open]);

  /* ---- options --------------------------------------------------------- */

  const modelOptions = useMemo<ComboboxOption[]>(
    () =>
      models
        .filter((model) => model.isActive || String(model.id) === form.modelId)
        .map((model) => ({
          value: String(model.id),
          label: model.isActive ? model.name : `${model.name} (retired)`,
        })),
    [models, form.modelId],
  );

  const gateShapeOptions = useMemo<ComboboxOption[]>(
    () =>
      gateShapes
        .filter((shape) => shape.isActive || String(shape.id) === form.gateShapeId)
        .map((shape) => ({
          value: String(shape.id),
          label: shape.isActive ? shape.name : `${shape.name} (retired)`,
        })),
    [gateShapes, form.gateShapeId],
  );

  /** The new business gate controls exist only where the API carries the keys. */
  const gateSupported = !isEdit || baseline.newBusinessGateBasis !== '';
  const gateUses = gateBasisUses(form.newBusinessGateBasis);

  const newBusinessGateShapeOptions = useMemo(
    () =>
      gateShapes
        .filter(
          (shape) => shape.isActive || String(shape.id) === form.newBusinessGateShapeId,
        )
        .map((shape) => ({
          value: String(shape.id),
          label: shape.isActive ? shape.name : `${shape.name} (retired)`,
        })),
    [gateShapes, form.newBusinessGateShapeId],
  );

  /**
   * ⚠️ THE STORED CODE IS INJECTED BACK RATHER THAN DROPPED. Reads never filter
   * on `isActive`, so a scheme created before tonight can hold a currency that
   * has since been retired — and a picker showing nothing for it would read as
   * "no currency set", which the next save would make true.
   */
  const currencyOptions = useMemo<ComboboxOption[]>(() => {
    const options = currencies.map((row) => ({
      value: row.currencyCode,
      label: `${row.currencyCode} — ${row.currencyName}`,
    }));
    const held = form.currency.trim();
    if (held !== '' && !options.some((option) => option.value === held)) {
      options.push({ value: held, label: `${held} (no longer active)` });
    }
    return options;
  }, [currencies, form.currency]);

  /**
   * The four bands plus the unbanded companies, and a stored slug this client
   * does not know about is appended rather than dropped.
   *
   * ⛔ NO LABEL MAP ANYWHERE. The four names are admin-editable columns on
   * `f_billing_region_defaults`, fetched through `useBillingRegionLabels`; a
   * literal `{united_kingdom: 'United Kingdom', …}` here would be a second copy
   * of wording the server owns, correct on the day and silently wrong after the
   * first rename.
   *
   * ⚠️ THE UNBANDED BAND SORTS LAST, by `isUnbandedRegion` rather than by its
   * position in a list somebody may reorder. It is a real population and a real
   * choice, never a placeholder for "none chosen" — that is `[]`.
   */
  const regionOptions = useMemo<MultiComboboxOption[]>(() => {
    const known = TARGET_BILLING_REGIONS.filter((region) => !isUnbandedRegion(region));
    const unbanded = TARGET_BILLING_REGIONS.filter((region) => isUnbandedRegion(region));
    const ordered: string[] = [...known, ...unbanded];

    for (const held of form.billingRegions) {
      if (!ordered.includes(held)) ordered.push(held);
    }

    return ordered.map((region) => ({
      value: region,
      // A slug this client does not know is labelled with itself — the same
      // documented fallback `useBillingRegionLabels` makes for an unread master.
      label: (TARGET_BILLING_REGIONS as readonly string[]).includes(region)
        ? labelOfRegion(region as TargetBillingRegion)
        : region,
    }));
  }, [form.billingRegions, labelOfRegion]);

  /* ---- diffing --------------------------------------------------------- */

  const regionsChanged = !sameSet(form.billingRegions, baseline.billingRegions);

  const patch = useMemo<SchemePatch | null>(() => {
    if (!isEdit) return null;
    const body: SchemePatch = {};

    /*
     * ⚠️ PERCENTAGES ARE COMPARED IN THEIR PAYLOAD FORM, so re-opening a scheme
     * stored as `"2.0000"` and leaving the box alone is not a change — and typing
     * `2` over `2.0000` is not one either. Comparing the raw strings would mark
     * every loaded scheme dirty the moment somebody touched an unrelated field,
     * and an edit that changed nothing would re-assert four rates on a template
     * that reaches every assignment on it.
     */
    const percentChanged = (key: keyof FormState): boolean =>
      toPercentPayload(String(form[key])) !== toPercentPayload(String(baseline[key]));

    if (form.name.trim() !== baseline.name.trim()) body.name = form.name.trim();
    if (form.description.trim() !== baseline.description.trim()) {
      // `''` clears on this field — `optionalText` collapses it server-side —
      // and `null` is what says so explicitly.
      body.description = form.description.trim() === '' ? null : form.description.trim();
    }
    if (form.modelId !== baseline.modelId && form.modelId !== '') {
      body.modelId = Number(form.modelId);
    }
    if (form.gateShapeId !== baseline.gateShapeId && form.gateShapeId !== '') {
      body.gateShapeId = Number(form.gateShapeId);
    }
    if (percentChanged('personalRatePercent')) {
      body.personalRatePercent = toPercentPayload(form.personalRatePercent);
    }
    if (percentChanged('regionalRatePercent')) {
      body.regionalRatePercent = toPercentPayload(form.regionalRatePercent);
    }
    if (percentChanged('personalGatePercent')) {
      body.personalGatePercent = toPercentPayload(form.personalGatePercent);
    }
    if (percentChanged('regionalGatePercent')) {
      body.regionalGatePercent = toPercentPayload(form.regionalGatePercent);
    }
    if (percentChanged('newBusinessRatePercent')) {
      body.newBusinessRatePercent = toPercentPayload(form.newBusinessRatePercent);
    }
    // New business gate: only on an API that carries the keys, only when moved.
    // Blank on an override = `null`, which clears it back to the scheme's term.
    // A hidden override (the basis does not read it) is never sent.
    if (baseline.newBusinessGateBasis !== '') {
      const uses = gateBasisUses(form.newBusinessGateBasis);
      if (
        form.newBusinessGateBasis !== baseline.newBusinessGateBasis &&
        form.newBusinessGateBasis !== ''
      ) {
        body.newBusinessGateBasis = form.newBusinessGateBasis;
      }
      for (const [key, used] of [
        ['newBusinessPersonalGatePercent', uses.personal],
        ['newBusinessRegionalGatePercent', uses.regional],
      ] as const) {
        if (!used) continue;
        const next = toOptionalPercentPayload(form[key]);
        if (next !== toOptionalPercentPayload(baseline[key])) body[key] = next;
      }
      if (uses.shape && form.newBusinessGateShapeId !== baseline.newBusinessGateShapeId) {
        body.newBusinessGateShapeId =
          form.newBusinessGateShapeId === '' ? null : Number(form.newBusinessGateShapeId);
      }
    }
    if (
      form.clawbackWindowMonths.trim() !== baseline.clawbackWindowMonths.trim() &&
      form.clawbackWindowMonths.trim() !== ''
    ) {
      body.clawbackWindowMonths = Number(form.clawbackWindowMonths.trim());
    }
    if (form.currency !== baseline.currency && form.currency !== '') {
      body.currency = form.currency;
    }
    if (form.includesDraftInvoices !== baseline.includesDraftInvoices) {
      body.includesDraftInvoices = form.includesDraftInvoices;
    }
    /*
     * ⛔ THE ONE THAT MATTERS. Sent only when the set MOVED — an absent key
     * leaves the stored bands alone, and `[]` clears them. A payload rebuilt
     * from state would send `[]` for a picker nobody opened.
     */
    if (regionsChanged) {
      body.billingRegions = form.billingRegions as TargetBillingRegion[];
    }
    if (form.isActive !== baseline.isActive) body.isActive = form.isActive;

    return body;
  }, [form, baseline, isEdit, regionsChanged]);

  const dirty = patch !== null && Object.keys(patch).length > 0;

  /*
   * Blanking a required box produces no patch key, so the diff stays empty and
   * Save would lock over a visibly empty field. Let submit through instead, so
   * `validate` reports it; the empty-patch guard in `handleSubmit` still stops a
   * `{}` reaching the API.
   */
  const requiredBlank =
    form.name.trim() === '' ||
    form.modelId === '' ||
    form.gateShapeId === '' ||
    form.currency === '';

  const submitDisabled = saving || (isEdit && !dirty && !requiredBlank);

  /* ---- setters --------------------------------------------------------- */

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => {
      if (!(key in current)) return current;
      const next = { ...current };
      delete next[key as string];
      return next;
    });
  }

  /* ---- validation ------------------------------------------------------ */

  function validate(state: FormState): Record<string, string> {
    const found: Record<string, string> = {};

    if (state.name.trim() === '') found.name = 'A name is required.';
    else if (state.name.trim().length > NAME_MAX) {
      found.name = `Keep this to ${NAME_MAX} characters.`;
    }

    if (state.description.trim().length > DESCRIPTION_MAX) {
      found.description = `Keep this to ${DESCRIPTION_MAX} characters.`;
    }

    if (state.modelId === '') found.modelId = 'Choose a model.';
    if (state.gateShapeId === '') found.gateShapeId = 'Choose a gate shape.';

    for (const key of [
      'personalRatePercent',
      'regionalRatePercent',
      'personalGatePercent',
      'regionalGatePercent',
    ] as const) {
      const message = validatePercent(state[key]);
      if (message) found[key] = message;
    }

    // A blank box over a record from an older API (no key at all) is left
    // alone rather than refused: nothing is sent for it.
    const newBusinessUntouchedBlank =
      state.newBusinessRatePercent.trim() === '' && baseline.newBusinessRatePercent === '';
    if (!newBusinessUntouchedBlank) {
      const message = validatePercent(state.newBusinessRatePercent);
      if (message) found.newBusinessRatePercent = message;
    }

    // The overrides are optional (blank = same as the scheme) and checked only
    // while the chosen basis reads them.
    const uses = gateBasisUses(state.newBusinessGateBasis);
    for (const [key, used] of [
      ['newBusinessPersonalGatePercent', uses.personal],
      ['newBusinessRegionalGatePercent', uses.regional],
    ] as const) {
      if (!used || state[key].trim() === '') continue;
      const message = validatePercent(state[key]);
      if (message) found[key] = message;
    }

    const months = state.clawbackWindowMonths.trim();
    if (months === '') {
      found.clawbackWindowMonths = 'A window is required. Use 0 for no clawback.';
    } else if (!/^\d+$/.test(months)) {
      found.clawbackWindowMonths = 'Enter a whole number of months. 0 means no clawback.';
    } else if (Number(months) > CLAWBACK_WINDOW_MAX_MONTHS) {
      found.clawbackWindowMonths = `The longest window is ${CLAWBACK_WINDOW_MAX_MONTHS} months.`;
    }

    if (state.currency === '') found.currency = 'Choose the currency this scheme pays in.';

    return found;
  }

  /* ---- submit ---------------------------------------------------------- */

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    /*
     * ⚠️ `stopPropagation` even though this form is not nested today. React
     * propagates by the REACT tree, so a submit from a dialog opened out of this
     * one would reach a form behind it — this repo created a half-filled deal
     * that way, with `tsc` and oxlint both green.
     */
    event.stopPropagation();
    if (saving) return;

    const clientErrors = validate(form);
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      setFormError(null);
      return;
    }

    setSaving(true);
    setErrors({});
    setFormError(null);
    setSavedWarning(null);

    try {
      if (record) {
        const body = patch ?? {};
        // Belt and braces: the button is disabled in this state, but an empty
        // patch is a guaranteed 422 and must never leave the client.
        if (Object.keys(body).length === 0) {
          setSaving(false);
          return;
        }

        const update: CommissionSchemeUpdateInput = body;
        const result = await api.updateCommissionScheme(record.id, update);
        onSaved(result.data);

        /*
         * 🔴 RENDERED AFTER THE SAVE, NAMING THE ASSIGNMENTS, AND THE DIALOG
         * STAYS OPEN TO CARRY IT. Closing on success would throw the one thing
         * away that says this edit reaches a year somebody is contracted
         * against. It is NOT a pre-save confirm: the write has already landed
         * and `onSaved` has already fired, so nothing here can block it.
         */
        const warning = readSchemeWarning(result.warning);
        if (warning) {
          setSavedWarning(warning);
          return;
        }
        onClose();
        return;
      }

      const create: CommissionSchemeCreateInput = {
        name: form.name.trim(),
        description: form.description.trim() === '' ? null : form.description.trim(),
        modelId: Number(form.modelId),
        gateShapeId: Number(form.gateShapeId),
        personalRatePercent: toPercentPayload(form.personalRatePercent),
        regionalRatePercent: toPercentPayload(form.regionalRatePercent),
        personalGatePercent: toPercentPayload(form.personalGatePercent),
        regionalGatePercent: toPercentPayload(form.regionalGatePercent),
        newBusinessRatePercent: toPercentPayload(form.newBusinessRatePercent),
        clawbackWindowMonths: Number(form.clawbackWindowMonths.trim()),
        currency: form.currency,
        includesDraftInvoices: form.includesDraftInvoices,
        /*
         * ⚠️ ON A CREATE THERE IS NOTHING TO LEAVE ALONE, so absent and `[]`
         * coincide and the set is always sent. It stops coinciding on a PATCH,
         * which is what `regionsChanged` above is for.
         */
        billingRegions: form.billingRegions as TargetBillingRegion[],
        isActive: form.isActive,
      };
      /*
       * The new business gate keys go out only when they differ from the
       * server defaults ('scheme', null overrides), so a create against an API
       * without them still succeeds when nobody touched the controls.
       */
      if (form.newBusinessGateBasis !== '' && form.newBusinessGateBasis !== 'scheme') {
        create.newBusinessGateBasis = form.newBusinessGateBasis;
      }
      if (gateUses.personal && form.newBusinessPersonalGatePercent.trim() !== '') {
        create.newBusinessPersonalGatePercent = toPercentPayload(
          form.newBusinessPersonalGatePercent,
        );
      }
      if (gateUses.regional && form.newBusinessRegionalGatePercent.trim() !== '') {
        create.newBusinessRegionalGatePercent = toPercentPayload(
          form.newBusinessRegionalGatePercent,
        );
      }
      if (gateUses.shape && form.newBusinessGateShapeId !== '') {
        create.newBusinessGateShapeId = Number(form.newBusinessGateShapeId);
      }

      const result = await api.createCommissionScheme(create);
      onSaved(result.data);
      onClose();
    } catch (error) {
      if (error instanceof ApiError) {
        /*
         * ⛔ PARTITIONED ON THIS FORM'S OWN WRITE KEYS. A path naming a key with
         * no control here would otherwise be marked on nothing while still
         * counting towards "one field needs attention, highlighted below".
         */
        const { fields, unroutable } = error.routeFieldErrors(KNOWN_WRITE_KEYS);
        setErrors(fields);

        const extras = unroutable.filter((line) => line !== error.message);

        if (error.status === 403) {
          // A 403 carries NO `details`, so it can only ever be a banner.
          setFormError('You do not have permission to save commission schemes.');
        } else if (error.status === 404) {
          setFormError('This scheme no longer exists.');
        } else if (error.status === 409) {
          // A duplicate name names the other scheme — render it verbatim.
          setFormError(error.message);
        } else if (extras.length > 0) {
          setFormError(`${error.message} — ${extras.join('; ')}`);
        } else if (unroutable.length > 0) {
          setFormError(error.message);
        } else if (Object.keys(fields).length > 0) {
          setFormError(null);
        } else {
          setFormError(error.message);
        }
      } else {
        setFormError('Could not reach the server. Check your connection and try again.');
      }
    } finally {
      setSaving(false);
    }
  }

  /* ---- render helpers -------------------------------------------------- */

  const selectedModel = models.find((model) => String(model.id) === form.modelId) ?? null;

  /**
   * ⛔ NOT HELPER TEXT — the fourth kind: A FACT ABOUT THE DATA THE READER
   * CANNOT OTHERWISE SEE, and a sweep must not delete it.
   *
   * Which model a scheme carries decides what it PAYS, and the only
   * machine-readable difference is `netsPersonalFromPool`. The model's NAME is
   * admin-editable free text, so the label on the control cannot be relied on to
   * carry the arithmetic. It renders only once a model is chosen, so it is a
   * statement about the current selection rather than standing advice.
   */
  const modelHint = selectedModel ? modelExplanation(selectedModel) : undefined;

  /**
   * ⛔ NOT HELPER TEXT — the fourth kind again, and migration 202 asks for it in
   * as many words: a scheme with no bands is a LEGAL personal-only scheme AND is
   * indistinguishable from somebody having forgotten to pick a region, "which is
   * why the builder states the consequence at the control rather than refusing".
   *
   * It renders ONLY while the list is empty, so it describes the state on screen
   * and disappears the moment the state does.
   */
  const emptyBandsNote =
    form.billingRegions.length === 0
      ? 'No bands: the regional pool is zero and every lead on this scheme is paid their personal commission alone.'
      : undefined;

  const mastersNote = mastersFailed
    ? 'The model, gate-shape and currency lists could not be read.'
    : undefined;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      dismissOnBackdrop={false}
      title={isEdit ? 'Edit commission scheme' : 'New commission scheme'}
      footer={
        savedWarning ? (
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              form={formId}
              loading={saving}
              disabled={submitDisabled}
            >
              {isEdit ? 'Save changes' : 'Create scheme'}
            </Button>
          </>
        )
      }
    >
      <form id={formId} noValidate onSubmit={handleSubmit} className="grid min-w-0 gap-3">
        {savedWarning && (
          <Notice
            tone="warning"
            role="status"
            title={
              savedWarning.message ??
              'Saved. This edit reaches assignments that have not been run.'
            }
          >
            {savedWarning.items.length > 0 && (
              <ul className="mt-1 grid gap-0.5">
                {savedWarning.items.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )}
          </Notice>
        )}

        {/*
          ⛔ NOT HELPER TEXT — the fourth kind, and this module's own vocabulary
          file says where it goes: on the scheme form, and BESIDE the server's
          list rather than instead of it. A scheme is a TEMPLATE whose terms are
          read when a run computes, not copied when an assignment is created, and
          the opposite reading is just as reasonable — a reader who assumes terms
          are pinned at assignment edits a rate believing it applies to next year
          only. EDIT ONLY: on a create there is nothing for it to reach.
        */}
        {isEdit && (
          <p
            className="text-xs text-fg-muted"
            title={TEMPLATE_EDIT_REACHES_ASSIGNMENTS_NOTE.detail}
          >
            {TEMPLATE_EDIT_REACHES_ASSIGNMENTS_NOTE.short}
          </p>
        )}

        {formError && (
          <Notice tone="danger" role="alert">
            {formError}
          </Notice>
        )}

        <Section title="Essentials">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Field label={LABELS.name ?? 'Name'} required error={errors.name}>
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    name="name"
                    value={form.name}
                    maxLength={NAME_MAX}
                    autoComplete="off"
                    disabled={saving}
                    invalid={Boolean(errors.name)}
                    onChange={(event) => setField('name', event.target.value)}
                  />
                )}
              </Field>
            </div>

            <div className="sm:col-span-2">
              <Field label={LABELS.description ?? 'Description'} error={errors.description}>
                {({ id, 'aria-describedby': describedBy }) => (
                  <Textarea
                    id={id}
                    aria-describedby={describedBy}
                    name="description"
                    rows={2}
                    maxLength={DESCRIPTION_MAX}
                    value={form.description}
                    disabled={saving}
                    invalid={Boolean(errors.description)}
                    onChange={(event) => setField('description', event.target.value)}
                  />
                )}
              </Field>
            </div>

            <Field
              label={LABELS.modelId ?? 'Model'}
              required
              hint={modelHint}
              error={errors.modelId ?? mastersNote}
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  aria-describedby={describedBy}
                  value={form.modelId}
                  options={modelOptions}
                  disabled={saving}
                  invalid={Boolean(errors.modelId)}
                  onChange={(value) => setField('modelId', value)}
                />
              )}
            </Field>

            <Field label={LABELS.gateShapeId ?? 'Gate shape'} required error={errors.gateShapeId}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  aria-describedby={describedBy}
                  value={form.gateShapeId}
                  options={gateShapeOptions}
                  disabled={saving}
                  invalid={Boolean(errors.gateShapeId)}
                  onChange={(value) => setField('gateShapeId', value)}
                />
              )}
            </Field>
          </div>
        </Section>

        <Section title="Rates and gates">
          {/*
            ⚠️ THE UNIT IS IN THE LABEL AND IN THE CONTROL, and that redundancy is
            the point rather than clutter. `2` and `0.02` are a factor of a
            hundred apart, both store, and both produce a well-formed payout —
            there is no figure downstream that looks wrong.
          */}
          <div className="grid gap-3 sm:grid-cols-2">
            {(
              [
                ['personalRatePercent', LABELS.personalRatePercent],
                ['regionalRatePercent', LABELS.regionalRatePercent],
                ['personalGatePercent', LABELS.personalGatePercent],
                ['regionalGatePercent', LABELS.regionalGatePercent],
                ['newBusinessRatePercent', LABELS.newBusinessRatePercent],
              ] as const
            ).map(([key, label]) => (
              <Field
                key={key}
                label={label ?? key}
                required={key !== 'newBusinessRatePercent' || baseline.newBusinessRatePercent !== ''}
                error={errors[key]}
              >
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    name={key}
                    value={form[key]}
                    inputMode="decimal"
                    autoComplete="off"
                    disabled={saving}
                    invalid={Boolean(errors[key])}
                    trailingSlot={
                      <span aria-hidden="true" className="pr-2 text-xs text-fg-subtle">
                        %
                      </span>
                    }
                    onChange={(event) => setField(key, event.target.value)}
                  />
                )}
              </Field>
            ))}
          </div>
        </Section>

        {gateSupported && (
          <Section title="New business gate">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label={LABELS.newBusinessGateBasis ?? 'New business gate'}
                error={errors.newBusinessGateBasis}
              >
                {({ id, 'aria-describedby': describedBy }) => (
                  <Select
                    id={id}
                    aria-describedby={describedBy}
                    name="newBusinessGateBasis"
                    value={form.newBusinessGateBasis}
                    disabled={saving}
                    invalid={Boolean(errors.newBusinessGateBasis)}
                    onChange={(event) =>
                      setField(
                        'newBusinessGateBasis',
                        event.target.value as FormState['newBusinessGateBasis'],
                      )
                    }
                  >
                    {NEW_BUSINESS_GATE_BASIS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              {gateUses.shape && (
                <Field
                  label={LABELS.newBusinessGateShapeId ?? 'New business gate shape'}
                  error={errors.newBusinessGateShapeId ?? mastersNote}
                >
                  {({ id, 'aria-describedby': describedBy }) => (
                    <Select
                      id={id}
                      aria-describedby={describedBy}
                      name="newBusinessGateShapeId"
                      value={form.newBusinessGateShapeId}
                      disabled={saving}
                      invalid={Boolean(errors.newBusinessGateShapeId)}
                      onChange={(event) => setField('newBusinessGateShapeId', event.target.value)}
                    >
                      <option value="">Same as the scheme</option>
                      {newBusinessGateShapeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              )}

              {(
                [
                  [
                    'newBusinessPersonalGatePercent',
                    LABELS.newBusinessPersonalGatePercent,
                    gateUses.personal,
                  ],
                  [
                    'newBusinessRegionalGatePercent',
                    LABELS.newBusinessRegionalGatePercent,
                    gateUses.regional,
                  ],
                ] as const
              )
                .filter(([, , used]) => used)
                .map(([key, label]) => (
                  <Field key={key} label={label ?? key} error={errors[key]}>
                    {({ id, 'aria-describedby': describedBy }) => (
                      <Input
                        id={id}
                        aria-describedby={describedBy}
                        name={key}
                        value={form[key]}
                        inputMode="decimal"
                        autoComplete="off"
                        disabled={saving}
                        invalid={Boolean(errors[key])}
                        trailingSlot={
                          <span aria-hidden="true" className="pr-2 text-xs text-fg-subtle">
                            %
                          </span>
                        }
                        onChange={(event) => setField(key, event.target.value)}
                      />
                    )}
                  </Field>
                ))}
            </div>
          </Section>
        )}

        <Section title="Payment and recovery">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={LABELS.currency ?? 'Payment currency'} required error={errors.currency}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  aria-describedby={describedBy}
                  value={form.currency}
                  options={currencyOptions}
                  disabled={saving}
                  invalid={Boolean(errors.currency)}
                  onChange={(value) => setField('currency', value)}
                />
              )}
            </Field>

            <Field
              label={LABELS.clawbackWindowMonths ?? 'Clawback window (months)'}
              required
              error={errors.clawbackWindowMonths}
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  name="clawbackWindowMonths"
                  value={form.clawbackWindowMonths}
                  inputMode="numeric"
                  autoComplete="off"
                  disabled={saving}
                  invalid={Boolean(errors.clawbackWindowMonths)}
                  onChange={(event) => setField('clawbackWindowMonths', event.target.value)}
                />
              )}
            </Field>

            <div className="sm:col-span-2 grid gap-2">
              {/*
                ⚠️ `description` here is `Checkbox`'s own secondary line and is
                the fourth kind: a draft invoice is one never sent and never seen
                by the customer, and clawback recovers on void, credit and
                non-payment — none of which a draft is. Nothing else on this
                screen says what the tick costs.
              */}
              <Checkbox
                label={LABELS.includesDraftInvoices ?? 'Count draft invoices'}
                description="A draft has not reached awaiting payment, and clawback cannot recover it."
                checked={form.includesDraftInvoices}
                disabled={saving}
                onChange={(event) => setField('includesDraftInvoices', event.target.checked)}
              />
              <Checkbox
                label={LABELS.isActive ?? 'Active'}
                checked={form.isActive}
                disabled={saving}
                onChange={(event) => setField('isActive', event.target.checked)}
              />
            </div>
          </div>
        </Section>

        <Section title="Regional pool">
          <Field
            label={LABELS.billingRegions ?? 'Billing bands measured'}
            hint={emptyBandsNote}
            error={errors.billingRegions}
          >
            {({ id, 'aria-describedby': describedBy }) => (
              <MultiCombobox
                id={id}
                aria-describedby={describedBy}
                values={form.billingRegions}
                options={regionOptions}
                disabled={saving}
                invalid={Boolean(errors.billingRegions)}
                selectAll
                onChange={(values) => setField('billingRegions', values)}
              />
            )}
          </Field>
        </Section>
      </form>
    </Modal>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="min-w-0 rounded-lg border border-border bg-surface">
      <legend className="mx-3 px-1 text-xs font-semibold text-fg">{title}</legend>
      <div className="min-w-0 px-3 pt-1 pb-3">{children}</div>
    </fieldset>
  );
}
